<script>
	import {onDestroy} from 'svelte';
	import API from '../../States/API';
	import Helper from '../../Models/Helper';
	import Slider from '../../States/Slider';
	import Session from '../../States/Session';

	export let index = 0;

	let properties = {};
	let active = false;
	let plans = [];
	/* Keeps the empty state from standing in for a loading state. */
	let loaded = false;

	/*
	 * Naming $Session inside the reactive statement is what keeps the cards in
	 * step with the checkboxes — reading it from a function called by the template
	 * hides the dependency from the compiler, so the bars used to be drawn once
	 * and never updated.
	 *
	 * This list endpoint only returns counts, not the passage codes, so progress
	 * is expressed in passages read. The exact day lives in Plan.svelte, which has
	 * the schedule itself; showing an estimate here would contradict it.
	 */
	$: progress = plans.map(plan => {
		const done = ($Session.plans && $Session.plans[plan.code]) || [];
		const total = plan.passages || 0;
		return {
			read: done.length,
			total,
			percent: total ? Math.round((done.length / total) * 100) : 0,
		};
	});

	async function load() {
		Helper.title('Leesplannen');
		const response = await API.plans();
		if (response.success && Array.isArray(response.plans)) plans = response.plans;
		/* Also on a failed request — otherwise the screen stays blank with no word. */
		loaded = true;
	}

	function openPlan(plan) {
		Slider.route(['plan', [plan.code]]);
	}

	function updateSlider(slides) {
		const was_active = active;
		active = Slider.check(index, 'plans', properties);
		if (active) {
			properties = Slider.properties(index);
			active = properties.active;
			if (!was_active && active && !plans.length) load();
		}
	}

	const unsubscribe = Slider.subscribe(updateSlider);
	onDestroy(unsubscribe);
</script>

<style>
	/*
	 * Het achtergrondbeeld van de pagina zacht laten doorschemeren, zoals het
	 * zoekscherm dat doet (Search.svelte). Het laagje zit op de box; div.content
	 * moet transparant, anders legt box.css er een dekkende laag bovenop.
	 *
	 * Op de eigen klasse van de box, niet op div.box.feature: dan winnen deze
	 * regels op specificiteit in plaats van op de laadvolgorde van bundle.css.
	 */
	div.box.plans {
		background: var(--box-feature-background-transparent);
	}

	div.box.plans div.content {
		background: transparent;
	}

	h1 {
		font-size: 2.5em;
		font-weight: 400;
		font-family: Bitter, serif;
		letter-spacing: -.025em;
		color: var(--primary-color);
		margin: 0 0 .3em 0;
	}

	div.plan-card {
		margin-bottom: 1.25em;
		padding: 1.1em 1.25em;
		border-radius: .5em;
		background: var(--box-background-focus);
		border: 1px solid var(--primary-background-light);
	}

	div.plan-card h2 {
		font-size: 1.2em;
		font-weight: 700;
		color: var(--primary-color);
		margin: 0 0 .25em 0;
	}

	div.plan-card p.description {
		font-size: .95em;
		color: var(--secondary-color);
		margin: 0 0 .8em 0;
		line-height: 1.5em;
	}

	div.plan-card div.progress-bar {
		height: .4em;
		border-radius: .2em;
		background: var(--tertiary-background-light);
		overflow: hidden;
	}

	div.plan-card div.progress-bar div.fill {
		height: 100%;
		border-radius: .2em;
		background: var(--tertiary-color);
		transition: width .3s ease-out;
	}

	/* A plan runs to 1093 readings, so the first few round down to 0% — keep a
	   sliver visible once anything is read, as div.percentage does. */
	div.plan-card div.progress-bar div.fill.any {
		min-width: .4em;
	}

	div.plan-card div.progress-info {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-top: .6em;
	}

	div.plan-card div.progress-info span {
		font-size: .8em;
		color: var(--secondary-color);
	}

	/* .button carries margin-bottom: 1em from ui.css, which inside this flex row
	   only leaves dead space along the bottom of the card. */
	div.plan-card div.progress-info .button {
		margin-bottom: 0;
	}

	div.empty {
		color: var(--secondary-color);
		margin-top: 2em;
	}

	div.empty i {
		font-size: 2em;
		display: block;
		margin-bottom: .5em;
		opacity: .4;
	}

	div.offline {
		color: var(--secondary-color);
		margin-top: 2em;
		line-height: 1.6em;
	}

	div.offline i {
		font-size: 1.5em;
		display: block;
		margin-bottom: .5em;
		opacity: .4;
	}
</style>

<div class="box feature plans" class:active>
	<div class="menu">
		<div class="begin">
			<div class="title">Leesplannen</div>
		</div>
		<div class="end">
			<div class="item" on:click="{event => Slider.close()}">
				<i class="fas fa-times"></i>
			</div>
		</div>
	</div>
	<div class="content">
		{#if $API.active}
			<h1>Leesplannen</h1>
			<p>Lees de Bijbel systematisch met een leesplan.</p>
			{#if Helper.is(plans)}
				{#each plans as plan, number}
					<div class="plan-card">
						<h2>{plan.title}</h2>
						<p class="description">{plan.description}</p>
						<div class="progress-bar">
							<div class="fill" class:any="{progress[number].read > 0}" style="width: {progress[number].percent}%;"></div>
						</div>
						<div class="progress-info">
							<span>
								{#if progress[number].read === 0}
									{plan.steps} dagen
								{:else}
									{progress[number].read} van {progress[number].total} gelezen
								{/if}
							</span>
							<div class="button tertiary" on:click="{event => openPlan(plan)}">
								{progress[number].read === 0 ? 'Beginnen' : 'Verder lezen'} <i class="fas fa-chevron-right"></i>
							</div>
						</div>
					</div>
				{/each}
			{:else if loaded}
				<!-- Only once the request has settled; before that this would be a
				     wrong answer rather than a loading state. -->
				<div class="empty">
					<i class="fad fa-list-ol"></i>
					Er zijn nog geen leesplannen beschikbaar.
				</div>
			{/if}
		{:else}
			<div class="offline">
				<i class="fad fa-wifi-slash"></i>
				Leesplannen zijn niet beschikbaar zonder internetverbinding.
			</div>
		{/if}
	</div>
</div>
